import { ApplicationAccessType, CommunicationType, WhatsApp, WhatsAppComponent } from 'domains/content/types';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';
import { actionTypes } from './whatsAppActions';
import { WhatsAppContentStateTypes } from './whatsAppTypes';
import { transformNumericPlaceholdersToMergeTags } from './utils/whatsAppUtils';

// Helper to normalize templateFields from string to Record<string, string>
const normalizeTemplateFields = (
    templateFields: string | Record<string, string> | undefined
): Record<string, string> => {
    if (!templateFields) return {};
    if (typeof templateFields === 'string') {
        try {
            return JSON.parse(templateFields);
        } catch {
            return {};
        }
    }
    return templateFields;
};

export const whatsAppInitialState: WhatsAppContentStateTypes = {
    name: 'Untitled',
    components: [],
    language: 'en',
    templateFields: {},
    defaultTemplateData: {},
    // WhatsApp templates default to InJourney and Commercial
    applicationsAccess: [ApplicationAccessType.INJOURNEY],
    communicationType: CommunicationType.COMMERCIAL,
    status: ContentStatus.DRAFT,
};

export const whatsAppReducer = (state: WhatsAppContentStateTypes, action: any): WhatsAppContentStateTypes => {
    switch (action.type) {
        case actionTypes.SET_NAME: {
            return {
                ...state,
                name: action.name,
            };
        }

        case actionTypes.SET_BODY_TEXT: {
            // Update BODY component when text changes
            const bodyComponentIndex = state.components.findIndex((component) => component.type === 'BODY');
            const bodyComponent: WhatsAppComponent = {
                type: 'BODY',
                text: action.text || '',
            };

            let updatedComponents: WhatsAppComponent[];
            if (bodyComponentIndex >= 0) {
                // Update existing BODY component
                updatedComponents = [...state.components];
                updatedComponents[bodyComponentIndex] = bodyComponent;
            } else {
                // Add new BODY component
                updatedComponents = [...state.components, bodyComponent];
            }

            return {
                ...state,
                components: updatedComponents,
            };
        }

        case actionTypes.SET_APPLICATIONS_ACCESS: {
            return {
                ...state,
                applicationsAccess: action.applicationsAccess,
            };
        }

        case actionTypes.SET_COMMUNICATION_TYPE: {
            return {
                ...state,
                communicationType: action.communicationType,
            };
        }

        case actionTypes.SET_WHATSAPP_STATE: {
            return {
                ...action.whatsAppState,
            };
        }

        default:
            return state;
    }
};

/**
 * Transforms component text from numeric placeholders to readable merge tag names.
 * Backend stores {{1}}, {{2}} but editor should display {{first_name}}, {{last_name}}.
 */
const transformComponentsWithMergeTags = (
    components: WhatsAppComponent[],
    templateFields: Record<string, string>
): WhatsAppComponent[] => {
    if (!templateFields || Object.keys(templateFields).length === 0) {
        return components;
    }

    return components.map((component) => {
        if (component.type === 'BODY' && component.text) {
            return {
                ...component,
                text: transformNumericPlaceholdersToMergeTags(component.text, templateFields),
            };
        }
        return component;
    });
};

// Helper function to initialize state from WhatsApp API data
export const initializeWhatsAppState = (
    whatsAppData: WhatsApp | Partial<WhatsApp> | null,
    isCopy: boolean
): WhatsAppContentStateTypes => {
    if (!whatsAppData || !('components' in whatsAppData && whatsAppData.components)) {
        return whatsAppInitialState;
    }

    const templateFields = normalizeTemplateFields(whatsAppData.templateFields || (whatsAppData as any).templateFields);
    // WhatsApp templates default to InJourney if no applicationsAccess is provided
    const applicationsAccess =
        typeof whatsAppData.applicationsAccess === 'string'
            ? (whatsAppData.applicationsAccess.split(',') as ApplicationAccessType[])
            : Array.isArray(whatsAppData.applicationsAccess)
            ? whatsAppData.applicationsAccess
            : [ApplicationAccessType.INJOURNEY];

    // Transform component text from {{1}} to {{first_name}} using templateFields
    const transformedComponents = transformComponentsWithMergeTags(whatsAppData.components || [], templateFields);

    return {
        name: isCopy ? `Copy of ${whatsAppData.name}` : whatsAppData.name || 'Untitled',
        components: transformedComponents,
        language: whatsAppData.language || 'en',
        templateFields,
        defaultTemplateData: whatsAppData.defaultTemplateData || {},
        applicationsAccess,
        communicationType: whatsAppData.communicationType || CommunicationType.COMMERCIAL,
        status: whatsAppData.status || ContentStatus.DRAFT,
        whatsappAccountId: whatsAppData.whatsappAccountId,
        whatsappNamespace: whatsAppData.whatsappNamespace,
        whatsappTemplateId: whatsAppData.whatsappTemplateId,
    };
};
